import { useState } from "react";

type BrandTheme = {
  bgFrom: string;
  bgTo: string;
  fg: string;
};

// Brand-accurate colour scheme per operator. Used for the monogram fallback
// AND the rounded chip behind the live logo image so transparent PNGs sit on
// the right brand-coloured surface.
const THEMES: Record<string, BrandTheme> = {
  betinia: { bgFrom: "#0f172a", bgTo: "#1e293b", fg: "#facc15" },
  leovegas: { bgFrom: "#ff5a00", bgTo: "#cc3a00", fg: "#ffffff" },
  betsson: { bgFrom: "#1f3a8a", bgTo: "#0f1f55", fg: "#ffffff" },
  unibet: { bgFrom: "#147a3d", bgTo: "#0c5026", fg: "#ffffff" },
  "mr-vegas": { bgFrom: "#7e22ce", bgTo: "#4c1d95", fg: "#fde68a" },
  rapidi: { bgFrom: "#0ea5e9", bgTo: "#0369a1", fg: "#ffffff" },
  lyrabet: { bgFrom: "#111827", bgTo: "#000000", fg: "#22d3ee" },
  instantspel: { bgFrom: "#10b981", bgTo: "#047857", fg: "#ffffff" },
  casinostugan: { bgFrom: "#1e40af", bgTo: "#1e3a8a", fg: "#fde68a" },
  snabbare: { bgFrom: "#dc2626", bgTo: "#991b1b", fg: "#ffffff" },
  nordicbet: { bgFrom: "#0c4a6e", bgTo: "#082f49", fg: "#fde68a" },
  spelahar: { bgFrom: "#475569", bgTo: "#334155", fg: "#ffffff" },
  hajper: { bgFrom: "#7c3aed", bgTo: "#5b21b6", fg: "#ffffff" },
};

// Local logo lookup. Real PNGs are downloaded once via
// `node scripts/fetch-logos.mjs` and committed under /public/brands/*.png.
// SVG monogram is served if the PNG is missing.
const PNG_BRANDS = new Set([
  "betinia",
  "leovegas",
  "betsson",
  "unibet",
  "mr-vegas",
  "lyrabet",
  "casinostugan",
  "snabbare",
  "nordicbet",
  "hajper",
]);

const SVG_BRANDS = new Set([
  "betinia",
  "leovegas",
  "betsson",
  "unibet",
  "mr-vegas",
  "rapidi",
  "lyrabet",
  "instantspel",
  "spelahar",
]);

function logoUrl(slug: string): string | null {
  if (PNG_BRANDS.has(slug)) return `/brands/${slug}.png`;
  if (SVG_BRANDS.has(slug)) return `/brands/${slug}.svg`;
  return null;
}

function initials(name: string): string {
  const cleaned = name.replace(/\.(se|com|net|eu)$/i, "").trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (cleaned.length <= 3) return cleaned.toUpperCase();
  return cleaned.slice(0, 2).toUpperCase();
}

type Props = {
  slug: string;
  name: string;
  size?: number;
  rounded?: "lg" | "xl" | "2xl";
  className?: string;
  /** Override the URL (e.g. value from `casino.logo_url`). */
  src?: string;
};

export function BrandLogo({
  slug,
  name,
  size = 56,
  rounded = "xl",
  className = "",
  src,
}: Props) {
  const theme = THEMES[slug] ?? {
    bgFrom: "#1f3a8a",
    bgTo: "#0f1f55",
    fg: "#ffffff",
  };
  const radiusClass =
    rounded === "lg" ? "rounded-lg" : rounded === "xl" ? "rounded-xl" : "rounded-2xl";
  const primary = src ?? logoUrl(slug);
  const fallback = SVG_BRANDS.has(slug) ? `/brands/${slug}.svg` : null;
  const [activeSrc, setActiveSrc] = useState<string | null>(primary);
  const [imgFailed, setImgFailed] = useState(false);

  function handleError() {
    if (activeSrc === primary && fallback && fallback !== primary) {
      // First image failed (e.g. PNG missing) — try the SVG monogram next.
      setActiveSrc(fallback);
      return;
    }
    setImgFailed(true);
  }

  return (
    <span
      className={`relative grid place-items-center overflow-hidden ${radiusClass} shadow-sm ring-1 ring-black/5 ${className}`}
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${theme.bgFrom}, ${theme.bgTo})`,
      }}
      aria-hidden
    >
      {activeSrc && !imgFailed ? (
        <img
          src={activeSrc}
          alt=""
          loading="lazy"
          decoding="async"
          width={size}
          height={size}
          onError={handleError}
          className="h-full w-full object-contain p-2"
        />
      ) : (
        <span
          className="font-extrabold tracking-tight"
          style={{ color: theme.fg, fontSize: size * 0.36 }}
        >
          {initials(name)}
        </span>
      )}
    </span>
  );
}
